/**
 * SignedActionModal — one modal for "this act is signed": a reason with a floor, plus a 21 CFR
 * Part 11 re-authentication, captured in the same submit.
 *
 * ux-production-supervisor round 1 #6 / round 2: a GMP counter-signature was one unguarded click
 * (`submitReview(id, "approve", "")`) while *rejecting* the same operation demanded a written
 * reason — the approval, which is the act that lets product move, was the cheaper of the two. The
 * MBMR lane had already shipped exactly this pattern for a lower-stakes act (return-for-correction:
 * a 10-character reason plus username/password re-auth), so the shape here deliberately matches
 * `MBMRConfig/ReturnForCorrectionModal.jsx` rather than inventing a second dialect.
 *
 * Deliberately not `ConfirmWithReason` with `requireEsign`: that flow verifies the password in a
 * separate call and hands the action only a username, which suits admin actions but leaves a
 * verified signature floating unattached. Here the caller decides — it receives
 * `{ reason, username, password }` and can either send the credentials with the act (the server's
 * preferred `esign: {username, password, comment}` contract in `services/approval/guards.py`) or
 * verify then pass `signed_by`, depending on which contract the endpoint speaks.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Input, Modal, Typography } from 'antd';
import { AlertTriangle, Lock, ShieldCheck, User } from 'lucide-react';
import useAppSetting from '@/hooks/useAppSetting';
import { ESIGN_REQUIRED_SETTING, ESIGN_BYPASS_USER, ESIGN_BYPASS_REASON } from '@/utils/esignSetting';

const { Text } = Typography;

/** Every e-signed remark in this product already requires ten characters; so does this one. */
export const MIN_SIGNED_REASON = 10;
export const MAX_SIGNED_REASON = 500; // the reason box's limit; a programmatic append may not pass it either

export default function SignedActionModal({
  open,
  title,
  intent = 'primary', // 'primary' | 'danger'
  confirmText = 'Confirm & sign',
  context = null, // rendered above the reason — what exactly is being signed
  reasonLabel = 'Reason for this decision',
  reasonPlaceholder = 'What are you signing, and why? This is written into the batch record verbatim.',
  submitting = false,
  error = null,
  onCancel,
  onSubmit,
  alwaysSign = false, // EXM-00: acts the server always signs afresh (e.g. a hold release) never auto-bypass
  initialReason = '', // a reason typed earlier and kept (e.g. the act was refused and the page reloaded); credentials never are
  reasonAppend = null, // { key, text }: words the person chose to add (e.g. a suggested basis) — appended once per key, then editable
}) {
  const esignSetting = useAppSetting(ESIGN_REQUIRED_SETTING, true);
  const esignRequired = alwaysSign || esignSetting;
  const bypassedRef = useRef(false);
  const [reason, setReason] = useState('');
  const [appendBlocked, setAppendBlocked] = useState(false); // the suggested words did not fit the limit and were not added
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason('');
      setUsername('');
      setPassword('');
      setTouched(false);
      bypassedRef.current = false;
    } else if (initialReason) {
      setReason(initialReason);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!(open && reasonAppend?.text)) { setAppendBlocked(false); return; }
    if (reason.includes(reasonAppend.text)) { setAppendBlocked(false); return; }   // already in the reason
    const next = reason.trim() ? `${reason.trimEnd()}\n${reasonAppend.text}` : reasonAppend.text;
    if (next.length > MAX_SIGNED_REASON) { setAppendBlocked(true); return; }   // never cut silently: say so, add nothing
    setAppendBlocked(false);
    setReason(next);
  }, [reasonAppend?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Global "E-Signature Required" is OFF — auto-complete instead of prompting for credentials.
  useEffect(() => {
    if (open && !esignRequired && !bypassedRef.current) {
      bypassedRef.current = true;
      onSubmit?.({ reason: ESIGN_BYPASS_REASON, username: ESIGN_BYPASS_USER, password: null });
    }
  }, [open, esignRequired, onSubmit]);

  const trimmed = reason.trim();
  const reasonInvalid = trimmed.length < MIN_SIGNED_REASON;
  const canSubmit = !reasonInvalid && username.trim() && password && !submitting;

  const submit = () => {
    setTouched(true);
    if (!canSubmit) return;
    onSubmit?.({ reason: trimmed, username: username.trim(), password });
  };

  return (
    <Modal
      open={open && esignRequired}
      centered
      maskClosable={false}
      destroyOnHidden
      width={560}
      zIndex={1600}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={18} />
          <span>{title}</span>
        </span>
      }
      okText={submitting ? 'Signing…' : confirmText}
      cancelText="Cancel"
      okButtonProps={{ danger: intent === 'danger', disabled: !canSubmit, loading: submitting }}
      onOk={submit}
      onCancel={submitting ? undefined : onCancel}
    >
      {context && <div style={{ marginBottom: 12 }}>{context}</div>}

      {error && (
        <Alert
          type="error"
          showIcon
          icon={<AlertTriangle size={16} />}
          style={{ marginBottom: 12 }}
          message={typeof error === 'string' ? error : error.message}
        />
      )}

      <Text strong style={{ fontSize: 13 }}>
        {reasonLabel}
      </Text>
      <Input.TextArea
        autoFocus
        rows={3}
        value={reason}
        maxLength={MAX_SIGNED_REASON}
        showCount
        onChange={(e) => setReason(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder={reasonPlaceholder}
        status={touched && reasonInvalid ? 'error' : undefined}
        data-testid="signed-action-reason"
        style={{ marginTop: 6 }}
      />
      {appendBlocked && (
        <Text type="danger" style={{ fontSize: 12 }} data-testid="signed-action-append-blocked">
          The suggested text does not fit in the {MAX_SIGNED_REASON}-character reason and was not added. Shorten the reason and use it again.
        </Text>
      )}
      {touched && reasonInvalid && (
        <Text type="danger" style={{ fontSize: 12 }}>
          At least {MIN_SIGNED_REASON} characters — this is the record of why you signed.
        </Text>
      )}

      <div style={{ marginTop: 14 }}>
        <Text strong style={{ fontSize: 13 }}>
          Electronic signature (21 CFR Part 11)
        </Text>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
          Re-enter your own credentials. Your name, the time and the reason above are written into the batch record.
        </div>
        <Input
          prefix={<User size={14} color="#6b7280" />}
          placeholder="Your username"
          value={username}
          autoComplete="off"
          onChange={(e) => setUsername(e.target.value)}
          style={{ marginBottom: 8 }}
          data-testid="signed-action-username"
        />
        <Input.Password
          prefix={<Lock size={14} color="#6b7280" />}
          placeholder="Your password"
          value={password}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
          onPressEnter={submit}
          data-testid="signed-action-password"
        />
      </div>
    </Modal>
  );
}

SignedActionModal.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.node,
  intent: PropTypes.oneOf(['primary', 'danger']),
  confirmText: PropTypes.string,
  context: PropTypes.node,
  reasonLabel: PropTypes.string,
  reasonPlaceholder: PropTypes.string,
  submitting: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  alwaysSign: PropTypes.bool,
  initialReason: PropTypes.string,
  reasonAppend: PropTypes.shape({ key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), text: PropTypes.string }),
};
